import theme from '@/assets/theme';
import CustomShipmentText from '@/components/CustomShipmentText';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { themeT } from '@/utils/themeText';
import { Flex } from 'antd';
import React, { memo } from 'react';
import ExternalMapAlertsForm from '../partials/ExternalMapAlertsForm';
import DisplayTimeDropdown from './DisplayTimeDropdown';
import PageContent from './PageContent';
import PizzaTracker from './PizzaTracker/PizzaTracker';
import ShareHeaderActions from './ShareHeaderActions';
import { DesktopShipmentDrawerProps } from './types';
import {
  AtRiskTringleIcon,
  ShareAlertIcon,
  getShipmentLegModeIcon,
} from '@/assets/icons/icons';
import { getLegModeIconType } from '../ControlTower/components/shipment/legModeUtils';
import ReportIssueCard from '../partials/ReportIssue/ReportIssueCard';
import { ExecutionLegDetail } from '@/apis/types';

const DesktopShipmentDrawer: React.FC<DesktopShipmentDrawerProps> = memo(
  (props) => {
    const {
      isVisible,
      isExpanded,
      showAlertForm,
      setShowAlertForm,

      cardVisibilityState,
      handleCardActions,
      canShowReportIssueCTA,

      handleClose,
      isSharePage,

      showAlerts,
      alertsData,
      alertIndex,
      setAlertIndex,
      uuid,
      shipment,
      shipmentId,
      legType,
      productDetails,
      pointState,
      capitalize,
      getFormattedTime,
      downloadFile,
      halfHeight,
      showFull,
      setShowFull,
      handleSetHalfHeight,
      arrowLink,
      CommentIcon,
      AtRisk,
      FileSvgIcon,
      openDropdown,
      setOpenDropdown,
      displayTime,
      onChangeDisplayTime,
      fetchSingleShipmentData,
      refetchSharePageData,
    } = props;

    const legMode =
      (legType ?? shipment?.leg_type) === 'EL'
        ? (shipment as unknown as { mode?: ExecutionLegDetail['mode'] })?.mode
        : undefined;
    const isExecutionLeg = legType === 'EL' || shipment?.leg_type === 'EL';

    return (
      <div
        className={`custom-card-drawer custom-map-card overide-custom-drawer ${
          showAlertForm && 'for-alert-form'
        } ${isVisible ? 'visible' : ''}`}
      >
        <div
          className={`custom-card-drawer-content external-card-height-auto ${
            isExpanded ? '' : 'mobile-collapsed'
          }`}
          style={{ position: 'relative', height: showAlertForm ? 'auto' : '' }}
        >
          {/* 1. ALERT FORM */}
          {showAlertForm ? (
            <div>
              <ExternalMapAlertsForm
                setShowAlertForm={handleClose}
                externalToken={uuid}
              />
            </div>
          ) : cardVisibilityState?.showCard ? (
            /* 2. REPORT ISSUE CARD (BLOCKS EVERYTHING ELSE) */
            <ReportIssueCard
              cardVisibilityState={cardVisibilityState}
              handleCardActions={handleCardActions}
              shipment={shipment}
              token={uuid}
              refetchSharePageData={refetchSharePageData}
            />
          ) : (
            /* 3. NORMAL CONTENT */
            <div>
              <Flex vertical gap={10}>
                <Flex gap={0} vertical className='custom-card-drawer-header'>
                  <Flex gap={8} align='center' justify='space-between'>
                    <CustomShipmentText
                      text={
                        isExecutionLeg
                          ? 'MBL'
                          : themeT('referenceId', theme.themeType)
                      }
                      fontWeight={600}
                      color='var(--text-secondary)'
                    />

                    {isSharePage ? (
                      <Flex gap={8} align='center'>
                        <ShareHeaderActions
                          onClick={() => setShowAlertForm(true)}
                          text='Get updates'
                          icon={<ShareAlertIcon />}
                        />

                        {canShowReportIssueCTA && (
                          <ShareHeaderActions
                            onClick={() => handleCardActions(true, false, '')}
                            text='Report issue'
                            icon={
                              <AtRiskTringleIcon
                                color={'#000'}
                                width={12}
                                height={12}
                              />
                            }
                          />
                        )}
                      </Flex>
                    ) : null}
                  </Flex>

                  <Flex gap={8} align='center'>
                    {isExecutionLeg ? (
                      <span
                        className='shipment-grid-reference-leg-icon'
                        style={{ flexShrink: 0, lineHeight: 0 }}
                        aria-hidden='true'
                      >
                        {getShipmentLegModeIcon(
                          getLegModeIconType(
                            legMode?.name ?? legMode?.value ?? ''
                          )
                        )}
                      </span>
                    ) : null}
                    <TruncateWithTooltip
                      text={
                        isExecutionLeg
                          ? (shipment?.mbl ?? '')
                          : (shipment?.reference_number ?? '')
                      }
                      max={23}
                      render={(t) => (
                        <span style={{ width: 'fit-content' }}>
                          <CustomShipmentText
                            text={t}
                            fontSize='18px'
                            fontWeight={700}
                            lineHeight='24px'
                            marginTop='0px'
                          />
                        </span>
                      )}
                    />
                  </Flex>
                  {isExecutionLeg && (
                    <CustomShipmentText
                      text='Execution leg'
                      color='rgb(118, 118, 118)'
                      fontWeight={500}
                      fontSize='10px'
                      marginTop='0px'
                    />
                  )}

                  {/* Simple Pizza Tracker */}
                  {isSharePage && (
                    <PizzaTracker
                      shipment={shipment}
                      selectedTimezone={displayTime?.value}
                    />
                  )}
                  {isSharePage && (
                    <DisplayTimeDropdown
                      value={displayTime}
                      onChange={onChangeDisplayTime}
                      open={openDropdown}
                      setOpen={setOpenDropdown}
                    />
                  )}
                </Flex>

                <PageContent
                  isSharePage={isSharePage}
                  variant='desktop'
                  showAlerts={showAlerts}
                  alertsData={alertsData}
                  alertIndex={alertIndex}
                  setAlertIndex={setAlertIndex}
                  shipment={shipment}
                  shipmentId={shipmentId}
                  productDetails={productDetails}
                  pointState={pointState}
                  capitalize={capitalize}
                  getFormattedTime={getFormattedTime}
                  downloadFile={downloadFile}
                  halfHeight={halfHeight}
                  setHalfHeightDirect={handleSetHalfHeight}
                  showFull={showFull}
                  setShowFull={setShowFull}
                  arrowLink={arrowLink}
                  CommentIcon={CommentIcon}
                  AtRisk={AtRisk}
                  FileSvgIcon={FileSvgIcon}
                  fetchSingleShipmentData={fetchSingleShipmentData}
                  uuid={uuid}
                />
              </Flex>
            </div>
          )}
        </div>
      </div>
    );
  }
);

DesktopShipmentDrawer.displayName = 'DesktopShipmentDrawer';
export default DesktopShipmentDrawer;
